import { resolveLimitTickCents, snapTradeCents } from '@/domain/market/limit-price-tick';
import { getBinaryOutcomeValues, tradeCentsToDollars } from '@/utils/market-outcomes';
import { normalizeOrderPrice, normalizeOrderQuantity } from '@/utils/order-payload-normalize';
import { validateTradeOrderMinLimits } from '@/utils/trade-order-validate';
import { ORDER_STATUS } from '@/constants/trade-order-status';

export { normalizeOrderPrice, normalizeOrderQuantity } from '@/utils/order-payload-normalize';

function decimalIntegerParts(value) {
  const [mantissa, exponent = '0'] = String(value).split('e');
  const [integer, fraction = ''] = mantissa.split('.');
  return { coefficient: BigInt(integer + fraction), scale: fraction.length - Number(exponent) };
}

/** 市价买入份额向上补齐六位，允许本金略超输入；手续费仅在下单 API 中计算。 */
export function marketBuyQuantity(amountDollars, priceDollars) {
  const amount = Number(amountDollars);
  const price = Number(priceDollars);
  if (!Number.isFinite(amount) || !Number.isFinite(price) || amount <= 0 || price <= 0) return 0;
  // 用十进制整数除法向上取整，避免浮点除法先丢精度或给整除结果多补一份微份额。
  const amountParts = decimalIntegerParts(amount);
  const priceParts = decimalIntegerParts(price);
  const shift = priceParts.scale - amountParts.scale + 6;
  const numerator = amountParts.coefficient * 10n ** BigInt(Math.max(shift, 0));
  const denominator = priceParts.coefficient * 10n ** BigInt(Math.max(-shift, 0));
  const microshares = (numerator + denominator - 1n) / denominator;
  const quantity = Number(`${microshares}e-6`);
  return Number.isFinite(quantity) ? quantity : 0;
}

/** 市价买入预估名义份额（展示用，不扣手续费、不做提交精度截断）。 */
export function estimateMarketBuyShares(amountDollars, priceDollars) {
  const amount = Number(amountDollars);
  const price = Number(priceDollars);
  if (!(amount > 0) || !(price > 0)) return 0;
  return amount / price;
}

function isActiveMarket(market) {
  const active = market?.active;
  return active === 1 || active === true || active === '1';
}

function pickPrimaryMarket(collection, market) {
  if (market?.id != null && String(market.id).trim()) return market;
  const markets = Array.isArray(collection?.markets) ? collection.markets : [];
  const active = markets.find((item) => isActiveMarket(item) && String(item?.id ?? '').trim());
  if (active) return active;
  return markets.find((item) => String(item?.id ?? '').trim()) ?? null;
}

/**
 * 下单 symbol：collection/get 返回的 markets[].id（非 collection id）
 * @param {Object} [params]
 * @param {Object} [params.collection] collection/get 或 page 单条 row
 * @param {Object} [params.market] 指定 market（如 binary 行）
 * @param {string|number} [params.explicitMarketId] 显式 market id，优先级最高
 */
export function resolveOrderSymbol({ collection, market, explicitMarketId } = {}) {
  const explicit = String(explicitMarketId ?? '').trim();
  if (explicit) return explicit;

  const picked = pickPrimaryMarket(collection, market);
  return String(picked?.id ?? '').trim();
}

/** 限价美分 → API 美元价（0.01 = 1¢） */
export function centsToOrderPrice(cents) {
  return tradeCentsToDollars(cents);
}

/** 根据弹窗选中项解析 outcome（使用 collection/get 返回的 outcomes 原值，不用 UI label） */
export function resolveOrderOutcome(order, outcomes = [], marketKind = 'multipleChoice') {
  const idx = Number(order?.outcomeIndex ?? 0);
  const opts = Array.isArray(outcomes) ? outcomes : [];
  const opt = opts[idx];

  if (opt?.outcome != null && String(opt.outcome).trim()) {
    return String(opt.outcome).trim();
  }

  const apiFromList = opts.map((item) => String(item?.outcome ?? '').trim()).filter(Boolean);
  if (apiFromList.length >= 2) {
    return apiFromList[Math.min(idx, apiFromList.length - 1)] ?? apiFromList[0];
  }

  const { yes, no } = getBinaryOutcomeValues(apiFromList);
  if (marketKind === 'binary' || opts.length === 2) {
    return idx === 0 ? yes : no;
  }

  return idx === 0 ? yes : no;
}

/** 买卖方向取反：推荐角色接口需传对手方向 */
export function oppositeOrderSide(side) {
  return String(side ?? '')
    .trim()
    .toUpperCase() === 'BUY'
    ? 'SELL'
    : 'BUY';
}

/** 规范化订单角色 */
export function normalizeTradeRole(role) {
  const r = String(role ?? '')
    .trim()
    .toUpperCase();
  return r === 'MAKER' || r === 'TAKER' ? r : '';
}

/**
 * PM venue orders use server submission — FE must skip client signing.
 * Historical name: this identifies the venue, NOT a successful/live status.
 *
 * Primary: liquiditySource=PM (platform create VO).
 * Compat only: status NEW when source omitted — retail SELF create stays PENDING;
 * never treat PARTIAL/FILLED as PM (SELF market fill paths must still activate).
 */
export function isPmVenueOrderLive(createdOrder) {
  if (!createdOrder || typeof createdOrder !== 'object') return false;
  const src = String(createdOrder.liquiditySource ?? createdOrder.liquidity_source ?? '')
    .trim()
    .toUpperCase();
  if (src === 'PM') return true;
  if (src && src !== 'PM') return false;
  const status = String(createdOrder.status ?? '')
    .trim()
    .toUpperCase();
  return status === 'NEW';
}

/** Creation may have persisted an order without completing venue submission. */
export function getPmOrderSubmissionState(order) {
  if (!isPmVenueOrderLive(order)) return null;
  const status = String(order?.status ?? '')
    .trim()
    .toUpperCase();
  if ([ORDER_STATUS.NEW, ORDER_STATUS.PARTIALLY_FILLED, ORDER_STATUS.FILLED].includes(status)) {
    return 'live';
  }
  if ([ORDER_STATUS.CANCELLED, ORDER_STATUS.REJECTED, ORDER_STATUS.EXPIRED].includes(status)) {
    return 'closed';
  }
  return 'pending';
}

function normalizeOrderSide(side) {
  return String(side ?? '')
    .trim()
    .toLowerCase();
}

/**
 * 将 TradeOrderDialog submit 载荷映射为创建订单 API 请求体
 * @param {Object} order TradeOrderDialog emit 的 order
 * @param {Object} ctx
 * @param {string} ctx.symbol collection/get 返回的 markets[].id
 * @param {string} ctx.userAddress 钱包地址
 * @param {string} [ctx.marketKind]
 * @param {string} [ctx.source='WEB']
 * @param {'MAKER'|'TAKER'} [ctx.tradeRole] 若已解析可直接带上
 * @param {string} [ctx.clientRequestId] stable idempotency key for this submit intent
 */
export function buildCreateOrderRequest(order, ctx = {}) {
  const symbol = String(ctx.symbol ?? '').trim();
  const userAddress = String(ctx.userAddress ?? '').trim();
  if (!symbol) throw new Error('symbol required');
  if (!userAddress) throw new Error('userAddress required');

  const isMarket = order.orderType === 'market';
  let priceCents = isMarket
    ? Number(order.avgPriceCents ?? order.limitPriceCents ?? 0)
    : Number(order.limitPriceCents ?? 0);
  // 限价：按盘口/官方 tick 吸附，避免 FairOdds 细精度或手输小数打到 PM 拒单
  if (!isMarket) {
    const tick = resolveLimitTickCents({
      outcomes: order.outcomes,
      tickSizeDollars: order.tickSizeDollars ?? ctx.tickSizeDollars,
    });
    priceCents = snapTradeCents(priceCents, tick);
  }
  const price = normalizeOrderPrice(centsToOrderPrice(priceCents));
  const sideRaw = normalizeOrderSide(order.side);

  let quantity = 0;
  const sellExactQty = Number(order.sellQuantityExact ?? 0);
  if (sideRaw === 'sell' && Number.isFinite(sellExactQty) && sellExactQty > 0) {
    quantity = normalizeOrderQuantity(sellExactQty);
  } else if (isMarket && sideRaw === 'buy') {
    // 输入金额只换算名义份额；费用、冻结与余额调整统一由 API 决定。
    quantity = marketBuyQuantity(order.amountDollars, price);
  } else if (isMarket) {
    const amount = Number(order.amountDollars ?? 0);
    quantity = price > 0 ? normalizeOrderQuantity(amount / price) : 0;
  } else {
    quantity = normalizeOrderQuantity(order.shares ?? 0);
  }

  if (!Number.isFinite(price) || price <= 0) throw new Error('invalid price');
  if (!Number.isFinite(quantity) || quantity <= 0) throw new Error('invalid quantity');

  const minLimitCheck = validateTradeOrderMinLimits(
    { ...order, shares: quantity, sellQuantityExact: quantity },
    ctx.pmProtocolVersion
  );
  if (!minLimitCheck.ok) {
    const err = new Error(minLimitCheck.code);
    err.tradeValidateCode = minLimitCheck.code;
    err.tradeMinShares = minLimitCheck.min;
    throw err;
  }

  const side = sideRaw === 'sell' ? 'SELL' : 'BUY';
  const type = isMarket ? 'MARKET' : 'LIMIT';
  const outcome = resolveOrderOutcome(order, order.outcomes, ctx.marketKind);
  const tradeRole = normalizeTradeRole(ctx.tradeRole);
  const clientRequestId = String(ctx.clientRequestId ?? '').trim();

  return {
    symbol,
    side,
    price,
    quantity,
    userAddress,
    type,
    outcome,
    ...(isMarket && side === 'BUY' ? { amount: Number(order.amountDollars) } : {}),
    ...(ctx.pmProtocolVersion === 'V2' && isMarket
      ? {
          executionType: 'FAK',
          amount: side === 'BUY' ? normalizeOrderQuantity(order.amountDollars) : 0,
          slippageBps: 1000,
        }
      : {}),
    ...(tradeRole ? { tradeRole } : {}),
    ...(clientRequestId ? { clientRequestId } : {}),
    ...(ctx.source ? { source: ctx.source } : { source: 'WEB' }),
  };
}
